import * as React from 'react'
import { EditCompanyGroupsProvider } from '../../context/EditCompanyGroupsContext'
import CompaniesInGroupTable from './management/CompaniesInGroupTable'
import useCompanyGroupService from '../../services/CompanyGroupService'
import { CompanyGroupModel } from '../../types/companyGroups'
import { faClose, FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { useEditCompanyGroups } from '../../context/EditCompanyGroupsContext'

interface EditCompanyGroupDetailsModalProps {
  group: CompanyGroupModel
  close: () => void
  companyId: string
  isDefaultGroup: boolean
  onGroupUpdated: () => void
}

const EditCompanyGroupDetailsModalContent: React.FC<{
  group: CompanyGroupModel
  close: () => void
  companyId: string
  isDefaultGroup: boolean
  onGroupUpdated: () => void
}> = ({ group, close, companyId, isDefaultGroup, onGroupUpdated }) => {
  const { t } = useLocalize()
  const { confirmEditGroupMembers, loading } = useEditCompanyGroups()
  const { removeGroups, changeGroupName } = useCompanyGroupService()
  const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false)
  const [newName, setNewName] = React.useState('')

  const handleConfirm = async () => {
    await confirmEditGroupMembers()
    onGroupUpdated()
  }

  const handleDelete = async () => {
    if (window.confirm(t('addCompany.confirmDeleteGroup'))) {
      const success = await removeGroups({ companyId, groupIds: [group.id] })
      if (success) {
        toast.success({ text: t('addCompany.groupDeleted') })
        onGroupUpdated()
      }
    }
  }

  const handleRename = async () => {
    if (!newName.trim()) {
      toast.error({ text: t('addCompany.groupNameEmpty') })
      return
    }

    const success = await changeGroupName({
      companyId,
      groupId: group.id,
      newName: newName.trim(),
    })

    if (success) {
      toast.success({ text: t('addCompany.groupRenamed') })
      setIsRenameModalOpen(false)
      onGroupUpdated()
    }
  }

  return (
    <>
      <div className="tw-fixed tw-inset-0 tw-z-[350] tw-flex tw-items-center tw-justify-center tw-overflow-auto tw-bg-content-base tw-bg-opacity-70">
        <div className="tw-flex tw-max-h-[600px] tw-w-full tw-max-w-[1000px] tw-flex-col tw-rounded-lg tw-bg-white">
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-p-4">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
                {group.name}
              </p>
              <p className="tw-text-sm tw-text-content-base/80">
                {t('addCompany.manageCompaniesInGroup')}
              </p>
            </div>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={() => close()}
            />
          </div>

          {/* Table */}
          <div className="tw-flex-1 tw-overflow-auto">
            <CompaniesInGroupTable companyId={companyId} />
          </div>

          {/* Info Box */}
          {!isDefaultGroup && (
            <div className="tw-border-t tw-border-gray-200 tw-bg-blue-50 tw-p-3">
              <p className="tw-text-sm tw-text-blue-800">
                {t('addCompany.companiesMultipleGroups')}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200 tw-p-4">
            <div className="tw-flex tw-gap-2">
              {!isDefaultGroup && (
                <>
                  <button
                    className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
                    onClick={() => setIsRenameModalOpen(true)}
                  >
                    {t('addCompany.renameGroup')}
                  </button>
                  <button
                    className="tw-rounded-lg tw-border tw-border-red-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-red-700 hover:tw-bg-red-50"
                    onClick={handleDelete}
                  >
                    {t('common.delete')}
                  </button>
                </>
              )}
            </div>
            <div className="tw-flex tw-gap-2">
              <button
                className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
                onClick={() => close()}
              >
                {t('common.cancel')}
              </button>
              <button
                className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? t('addCompany.saving') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rename Modal */}
      {isRenameModalOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[400] tw-flex tw-items-center tw-justify-center tw-bg-content-base tw-bg-opacity-70">
          <div className="tw-w-full tw-max-w-[400px] tw-rounded-lg tw-bg-white tw-p-6">
            <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">{t('addCompany.renameGroup')}</h3>
            <input
              type="text"
              defaultValue={group.name}
              onChange={(e) => setNewName(e.target.value)}
              className="tw-mb-4 tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary"
              placeholder={t('addCompany.newGroupName')}
            />
            <div className="tw-flex tw-justify-end tw-gap-2">
              <button
                className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
                onClick={() => setIsRenameModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90"
                onClick={handleRename}
              >
                {t('addCompany.rename')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditCompanyGroupDetailsModal: React.FC<EditCompanyGroupDetailsModalProps> = (props) => {
  return (
    <EditCompanyGroupsProvider companyId={props.companyId} group={props.group}>
      <EditCompanyGroupDetailsModalContent {...props} />
    </EditCompanyGroupsProvider>
  )
}

export default EditCompanyGroupDetailsModal
